
import { supabase } from "@/integrations/supabase/client";
import { articleMediaService } from "./articleMediaService";
import { articleTagService } from "./articleTagService";
import { articleBlockService } from "./articleBlockService";
import { ArticleData } from "./types";

export const articleCreationService = {
  async initializeStorageBuckets() {
    try {
      // Instead of trying to create buckets (which requires admin rights),
      // we'll just check if we have access to them and return a success
      // Storage buckets should be created by an administrator in the Supabase dashboard
      console.log("Checking storage access - buckets should be created by an admin in Supabase");
      return { error: null };
    } catch (error) {
      console.error('Error checking storage access:', error);
      return { error };
    }
  },
  
  async createArticle(article: ArticleData) {
    try {
      // Get the current authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error("Authentication required to create an article");
      }
      
      const authorId = user.id;
      
      // Extract the article data for the articles table
      const { 
        title, 
        content,
        excerpt,
        status,
        access_type,
        price,
        read_time,
        cover_image,
        subtitle,
        seo_title,
        seo_description,
        is_paywalled,
        featured_image_url,
        featured_image_alt,
        language,
        tags,
        ...metadataFields
      } = article;

      // First, create the article (without content - will be in blocks)
      const { data, error } = await supabase
        .from('articles')
        .insert({
          author_id: authorId,
          title: title || 'Untitled Article',
          subtitle: subtitle || null,
          excerpt: excerpt || null,
          status: status || 'draft',
          // Content now stored as blocks
          reading_time: read_time || 0,
          is_paywalled: is_paywalled || false,
          payment_type: access_type || 'free',
          micropayment_price: price || null,
          language: language || 'en',
          seo_title: seo_title || null,
          seo_description: seo_description || null,
          featured_image_url: featured_image_url || cover_image || null,
          featured_image_alt: featured_image_alt || null
        })
        .select('id')
        .single();

      if (error) {
        console.error('Error creating article:', error);
        throw error;
      }

      // Create blocks for the article
      if (data?.id) {
        // Handle string content (create a single paragraph block)
        if (typeof content === 'string') {
          await articleBlockService.createBlock({
            article_id: data.id,
            type: 'paragraph',
            content: { text: content },
            position: 0
          });
        } 
        // Handle EditorJS format content (convert blocks)
        else if (content && content.blocks) {
          const { data: blocks } = await articleBlockService.convertEditorJSToBlocks(data.id, content);
          
          if (blocks && blocks.length > 0) {
            for (const block of blocks) {
              await articleBlockService.createBlock(block);
            }
          }
        }

        // If there are tags, process them
        if (tags && tags.length > 0) {
          await articleTagService.updateArticleTags(data.id, tags);
        }

        // Process any media in the content blocks
        if (content && typeof content !== 'string' && content.blocks) {
          await articleMediaService.processArticleMedia(data.id, content.blocks, authorId);
        }
      }

      return { data, error: null };
    } catch (error) {
      console.error('Error creating article:', error);
      return { data: null, error };
    }
  }
};
